import { UNIQUE_POSSIBILITIES } from "@/constants/constants";
import { devLog } from "@/helper/helper";
import { Household, UPSettings, UPRow } from "@/types/types";

export async function calculateData(
  settings: UPSettings,
  clients: Household | null,
  gridSet: any,
  startRow: number
) {
  let data: any;
  const newGrid = structuredClone(gridSet);

  if (!clients) {
    return [];
  }

  if (newGrid?.length) {
    data = await renderData(settings, clients, startRow, newGrid);
  } else {
    data = await initData(settings, clients);
  }
  return data;
}

function calculateAgeWithOffset(baseAge: number, offset: number, maxAge = 120): number {
  return Math.min(baseAge + offset, maxAge);
}

// Create gridset
async function initData(settings: UPSettings, clients: Household) {
  const data: any = [];

  await new Promise((resolve) => {
    let i = 0;
    let startAt = UNIQUE_POSSIBILITIES.MinAge;
    let startAt2 = clients?.secondaryContact ? UNIQUE_POSSIBILITIES.MinAge : 101;

    if (!clients?.primaryContact) {
      return [];
    }

    const currentPrimaryAge = calculateAge(clients?.primaryContact?.birthDate?.toString());
    const currentSecondaryAge = clients?.secondaryContact
      ? calculateAge(clients?.secondaryContact?.birthDate.toString())
      : null;

    if (currentSecondaryAge) {
      let to62 = 0;
      if (
        currentPrimaryAge.value > UNIQUE_POSSIBILITIES.MinAge ||
        currentSecondaryAge.value > UNIQUE_POSSIBILITIES.MinAge
      ) {
        if (currentPrimaryAge.value > currentSecondaryAge.value) {
          to62 = currentPrimaryAge.value - UNIQUE_POSSIBILITIES.MinAge;
        } else {
          to62 = currentSecondaryAge.value - UNIQUE_POSSIBILITIES.MinAge;
        }
      }

      startAt = currentPrimaryAge.value - to62;
      startAt2 = currentSecondaryAge.value - to62;
    } else if (currentPrimaryAge.value < UNIQUE_POSSIBILITIES.MinAge) {
      startAt = currentPrimaryAge.value;
    }

    while (true) {
      if (startAt > 100 && startAt2 && startAt2 > 100 && currentPrimaryAge.value < 90) {
        resolve(null);
        break;
      } else if (startAt > 110 && startAt2 && startAt2 > 110 && currentPrimaryAge.value >= 90) {
        resolve(null);
        break;
      }

      if (i === 0) {
        const p1Data = renderP1InitData(clients, i);
        const p2Data = renderP2InitData(clients, i);

        let SocialSecurity1 = 0;

        if (p1Data.Age && settings.person1.beginAt <= p1Data.Age.value) {
          SocialSecurity1 = settings.person1.amount;
        }

        let SocialSecurity2 = 0;

        if (settings.person2 && p2Data.Age2 && settings.person2.beginAt <= p2Data.Age2.value) {
          SocialSecurity2 = settings.person2.amount;
        }

        const SocialSecurity = SocialSecurity1 + SocialSecurity2;

        data.push({
          id: i,
          Age: {
            value: startAt,
            isIncremented:
              startAt === currentPrimaryAge.value && currentPrimaryAge.isIncremented ? true : false,
          },
          Age2: {
            value: startAt2,
            isIncremented:
              currentSecondaryAge &&
              startAt2 === currentSecondaryAge.value &&
              currentSecondaryAge.isIncremented
                ? true
                : false,
          },
          SocialSecurity: {
            value: SocialSecurity ? SocialSecurity : 0,
            defaultValue: SocialSecurity ? SocialSecurity : 0,
            manualChange: false,
            note: "",
          },
          SocialSecurity1: {
            value: SocialSecurity1 ? SocialSecurity1 : 0,
            defaultValue: SocialSecurity1 ? SocialSecurity1 : 0,
            manualChange: false,
            note: "",
          },
          SocialSecurity2: {
            value: SocialSecurity2 ? SocialSecurity2 : 0,
            defaultValue: SocialSecurity2 ? SocialSecurity2 : 0,
            manualChange: false,
            note: "",
          },
          Retirement: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          ROR: { value: settings.ror, defaultValue: settings.ror, manualChange: false, note: "" },
          Pension: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          Income: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          Income2: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          Spending: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          Spending2: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          Spending3: { value: 0, defaultValue: 0, manualChange: false, note: "" },
          YearEnd: SocialSecurity,
        });
      } else {
        const p1Data = renderP1Data(clients, i);
        const p2Data = renderP2Data(clients, i);

        let SocialSecurity1 = 0;

        const ss1Cola = settings?.colaSS1 ?? settings.cola;
        const ss2Cola = settings?.colaSS2 ?? settings.cola;
        const income1Cola = settings?.colaIncome ?? settings.cola;
        const income2Cola = settings?.colaIncome2 ?? settings.cola;

        if (settings.person1?.beginAt === p1Data.Age?.value) {
          SocialSecurity1 = settings.person1.amount;
        } else if (
          settings.person1?.beginAt &&
          p1Data.Age &&
          settings.person1?.beginAt < p1Data.Age.value
        ) {
          SocialSecurity1 = data[i - 1]?.SocialSecurity1.value * (1 + ss1Cola);
        }

        let SocialSecurity2 = 0;

        if (settings.person2 && settings.person2?.beginAt === p2Data.Age2?.value) {
          SocialSecurity2 = settings.person2.amount;
        } else if (
          settings.person2?.beginAt &&
          p2Data.Age2 &&
          settings.person2?.beginAt < p2Data.Age2.value
        ) {
          SocialSecurity2 = data[i - 1]?.SocialSecurity2.value * (1 + ss2Cola);
        }

        const SocialSecurity = SocialSecurity1 + SocialSecurity2;

        data.push({
          id: i,
          Age: {
            value: startAt,
            isIncremented:
              startAt === currentPrimaryAge.value && currentPrimaryAge.isIncremented ? true : false,
          },
          Age2: {
            value: startAt2,
            isIncremented:
              currentSecondaryAge &&
              startAt2 === currentSecondaryAge.value &&
              currentSecondaryAge.isIncremented
                ? true
                : false,
          },
          SocialSecurity: {
            value: SocialSecurity ? SocialSecurity : 0,
            defaultValue: SocialSecurity ? SocialSecurity : 0,
            note: "",
          },
          SocialSecurity1: {
            value: SocialSecurity1 ? SocialSecurity1 : 0,
            defaultValue: SocialSecurity1 ? SocialSecurity1 : 0,
            note: "",
            manualChange: false,
          },
          SocialSecurity2: {
            value: SocialSecurity2 ? SocialSecurity2 : 0,
            defaultValue: SocialSecurity2 ? SocialSecurity2 : 0,
            note: "",
            manualChange: false,
          },
          Pension: {
            value: data[i - 1].Pension.value,
            defaultValue: data[i - 1].Pension.defaultValue,
            manualChange: false,
            note: "",
          },
          Retirement: {
            value: data[i - 1].YearEnd,
            defaultValue: data[i - 1].YearEnd,
            manualChange: false,
            note: "",
          },
          ROR: { value: settings.ror, defaultValue: settings.ror, manualChange: false, note: "" },
          Income: {
            value: data[i - 1].Income.value * (1 + income1Cola),
            defaultValue: data[i - 1].Income.value * (1 + income1Cola),
            manualChange: false,
            note: "",
          },
          Income2: {
            value: data[i - 1].Income2.value * (1 + income2Cola),
            defaultValue: data[i - 1].Income2.value * (1 + income2Cola),
            manualChange: false,
            note: "",
          },
          Spending: {
            value: data[i - 1].Spending.value * (1 + settings.inflation),
            defaultValue: data[i - 1].Spending.value * (1 + settings.inflation),
            manualChange: false,
            note: "",
          },
          Spending2: {
            value: data[i - 1].Spending2.value * (1 + settings.inflation),
            defaultValue: data[i - 1].Spending2.value * (1 + settings.inflation),
            manualChange: false,
            note: "",
          },
          Spending3: {
            value: data[i - 1].Spending3.value * (1 + settings.inflation),
            defaultValue: data[i - 1].Spending3.value * (1 + settings.inflation),
            manualChange: false,
            note: "",
          },
          YearEnd:
            data[i - 1].YearEnd * (1 + settings.ror) +
            SocialSecurity +
            data[i - 1].Pension.value +
            data[i - 1].Income.value * (1 + income1Cola) +
            data[i - 1].Income2.value * (1 + income2Cola) -
            data[i - 1].Spending.value * (1 + settings.inflation) -
            data[i - 1].Spending2.value * (1 + settings.inflation) -
            data[i - 1].Spending3.value * (1 + settings.inflation),
        });
      }

      i++;
      startAt++;
      if (startAt2) startAt2++;
    }
  });

  return data;
}

// Update
async function renderData(settings: UPSettings, clients: Household, startRow: any, gridSet: any) {
  const data: any = [];
  let iterator = 0;

  await new Promise((resolve) => {
    let i = startRow;

    if (!clients?.primaryContact) return [];

    const currentPrimaryAge = calculateAge(clients?.primaryContact.birthDate.toString(), 0);
    const currentSecondaryAge = clients?.secondaryContact
      ? calculateAge(clients?.secondaryContact.birthDate.toString(), 0)
      : null;

    let startAt = UNIQUE_POSSIBILITIES.MinAge;
    let startAt2 = clients?.secondaryContact ? UNIQUE_POSSIBILITIES.MinAge : 101;

    if (currentSecondaryAge) {
      let to62 = 0;
      if (
        currentPrimaryAge.value > UNIQUE_POSSIBILITIES.MinAge ||
        currentSecondaryAge.value > UNIQUE_POSSIBILITIES.MinAge
      ) {
        to62 =
          Math.max(currentPrimaryAge.value, currentSecondaryAge.value) -
          UNIQUE_POSSIBILITIES.MinAge;
      }

      startAt = calculateAgeWithOffset(currentPrimaryAge.value, -to62);
      startAt2 = calculateAgeWithOffset(currentSecondaryAge.value, -to62);
    } else if (currentPrimaryAge.value < UNIQUE_POSSIBILITIES.MinAge) {
      startAt = currentPrimaryAge.value;
    }

    for (let age = startRow; age <= 200; age++) {
      const age = calculateAgeWithOffset(startAt, i);
      const age2 = calculateAgeWithOffset(startAt2, i);

      const maxAgePrimary = currentPrimaryAge.value >= 90 ? 110 : 100;
      const maxAgeSecondary = currentSecondaryAge?.value
        ? currentSecondaryAge?.value >= 90
          ? 110
          : 100
        : currentPrimaryAge.value;

      if (!clients.isSingle) {
        if (!age || !age2) {
          resolve(null);
          break;
        }
        if (Math.min(age, age2) > Math.min(maxAgePrimary, maxAgeSecondary)) {
          devLog(
            "For Household with 2 clients, Max Age Primary: ",
            maxAgePrimary,
            "Max Age Secondary: ",
            maxAgeSecondary,
            "Household:",
            clients
          );

          devLog("Ending at: ", gridSet[i - 1]);

          break;
        }
      } else {
        if (!age) {
          resolve(null);
          break;
        }

        if (age > maxAgePrimary) {
          devLog("For Single Household, Max Age Primary: ", maxAgePrimary, "Household:", clients);
          devLog("Ending at: ", gridSet[i - 1]);
          break;
        }
      }

      if (i === startRow) {
        let SocialSecurity1 = 0;
        let SocialSecurity2 = 0;

        const contactsSwapped =
          gridSet[i].Age2.value === startAt && gridSet[i].Age.value === startAt2;

        if (
          settings.person1?.beginAt === gridSet[i].Age.value &&
          !gridSet[i].SocialSecurity1.manualChange
        ) {
          SocialSecurity1 = settings.person1.amount;
        } else if (
          gridSet[i].SocialSecurity1.manualChange ||
          settings.person1?.beginAt < gridSet[i].Age.value
        ) {
          SocialSecurity1 = gridSet[i].SocialSecurity1.value;
        }

        if (settings.person2?.beginAt && gridSet[i].Age2) {
          if (
            settings.person2?.beginAt === gridSet[i].Age2.value &&
            !gridSet[i].SocialSecurity1.manualChange
          ) {
            SocialSecurity2 = settings.person2.amount;
          } else if (
            gridSet[i].SocialSecurity1.manualChange ||
            settings.person2?.beginAt < gridSet[i].Age2.value
          ) {
            SocialSecurity2 = gridSet[i].SocialSecurity2.value;
          }
        }

        const SocialSecurity = SocialSecurity1 + SocialSecurity2;
        const RORVal = gridSet[i]?.ROR?.manualChange ? gridSet[i].ROR.value : settings.ror;

        data.push({
          id: i,
          Age: {
            value: age,
            isIncremented: contactsSwapped
              ? gridSet[i].Age2.value === currentPrimaryAge.value &&
                currentPrimaryAge?.isIncremented
              : gridSet[i].Age.value === currentPrimaryAge.value &&
                currentPrimaryAge?.isIncremented,
          },
          Age2: {
            value: age2,
            isIncremented: contactsSwapped
              ? gridSet[i].Age.value === currentSecondaryAge?.value &&
                currentSecondaryAge?.isIncremented
              : gridSet[i].Age2.value === currentSecondaryAge?.value &&
                currentSecondaryAge?.isIncremented,
          },
          SocialSecurity: {
            value: SocialSecurity ? SocialSecurity : 0,
            defaultValue: SocialSecurity ? SocialSecurity : 0,
          },
          SocialSecurity1: {
            value: SocialSecurity1 ? SocialSecurity1 : 0,
            defaultValue: gridSet[i].SocialSecurity1.defaultValue,
            manualChange: gridSet[i].SocialSecurity1.manualChange,
            note: gridSet[i].SocialSecurity1?.note ?? "",
          },
          SocialSecurity2: {
            value: SocialSecurity2 ? SocialSecurity2 : 0,
            defaultValue: gridSet[i].SocialSecurity2.defaultValue,
            manualChange: gridSet[i].SocialSecurity2.manualChange,
            note: gridSet[i].SocialSecurity2?.note ?? "",
          },
          Retirement: {
            value: gridSet[i].Retirement.value ?? 0,
            defaultValue: gridSet[i].Retirement.defaultValue,
            manualChange: gridSet[i]?.Retirement?.manualChange,
            note: gridSet[i]?.Retirement?.note ?? "",
          },
          ROR: {
            value: RORVal,
            defaultValue: gridSet[i].ROR.defaultValue,
            manualChange: gridSet[i]?.ROR?.manualChange,
            note: gridSet[i]?.ROR?.note ?? "",
          },
          Pension: {
            value: gridSet[i].Pension.value ?? 0,
            defaultValue: gridSet[i].Pension.defaultValue,
            manualChange: gridSet[i]?.Pension?.manualChange,
            note: gridSet[i]?.Pension?.note ?? "",
            isExcluded: gridSet[i].Pension.isExcluded ?? undefined,
          },
          Income: {
            value: gridSet[i].Income.value ?? 0,
            defaultValue: gridSet[i].Income.defaultValue,
            manualChange: gridSet[i]?.Income?.manualChange,
            note: gridSet[i]?.Income?.note ?? "",
            isExcluded: gridSet[i].Income.isExcluded ?? undefined,
          },
          Income2: {
            value: gridSet[i].Income2.value ?? 0,
            defaultValue: gridSet[i].Income2.defaultValue,
            manualChange: gridSet[i]?.Income2?.manualChange,
            note: gridSet[i]?.Income2?.note ?? "",
            isExcluded: gridSet[i].Income2.isExcluded ?? undefined,
          },
          Spending: {
            value: gridSet[i].Spending.value ?? 0,
            defaultValue: gridSet[i].Spending.defaultValue,
            manualChange: gridSet[i]?.Spending?.manualChange,
            note: gridSet[i]?.Spending?.note ?? "",
            isExcluded: gridSet[i].Spending.isExcluded ?? undefined,
          },
          Spending2: {
            value: gridSet[i].Spending2.value ?? 0,
            defaultValue: gridSet[i].Spending2.defaultValue,
            manualChange: gridSet[i]?.Spending2?.manualChange,
            note: gridSet[i]?.Spending2?.note ?? "",
            isExcluded: gridSet[i].Spending2.isExcluded ?? undefined,
          },
          Spending3: {
            value: gridSet[i].Spending3.value ?? 0,
            defaultValue: gridSet[i].Spending3.defaultValue,
            manualChange: gridSet[i]?.Spending3?.manualChange,
            note: gridSet[i]?.Spending3?.note ?? "",
            isExcluded: gridSet[i].Spending3.isExcluded ?? undefined,
          },
          YearEnd:
            gridSet[i].Retirement.value * (1 + RORVal) +
            SocialSecurity +
            gridSet[i].Pension.value +
            gridSet[i].Income.value +
            gridSet[i].Income2.value -
            gridSet[i].Spending.value -
            gridSet[i].Spending2.value -
            gridSet[i].Spending3.value,
        });
      } else {
        let SocialSecurity1 = 0;
        let SocialSecurity2 = 0;

        const contactsSwapped =
          gridSet[i]?.Age2?.value === startAt + i && gridSet[i]?.Age?.value === startAt2 + i;
        let newAge = contactsSwapped ? gridSet[i]?.Age2?.value : gridSet[i]?.Age?.value;
        let newAge2 = contactsSwapped ? gridSet[i]?.Age?.value : (gridSet[i]?.Age2?.value ?? 0);

        if (!gridSet[i]) {
          newAge = age;
          newAge2 = age2;
          gridSet[i] = {
            Age: { value: newAge },
            Age2: { value: newAge2 },
            Retirement: { value: 0, manualChange: false, note: "" },
            ROR: { value: settings.ror, manualChange: false, note: "" },
            Pension: { value: 0, manualChange: false, note: "" },
            Spending: { value: 0, manualChange: false, note: "" },
            Spending2: { value: 0, manualChange: false, note: "" },
            Spending3: { value: 0, manualChange: false, note: "" },
            Income: { value: 0, manualChange: false, note: "" },
            Income2: { value: 0, manualChange: false, note: "" },
            SocialSecurity: { value: 0, manualChange: false, note: "" },
            SocialSecurity1: { value: 0, manualChange: false, note: "" },
            SocialSecurity2: { value: 0, manualChange: false, note: "" },
            id: i,
          };
        }

        const ss1Cola = settings?.colaSS1 ?? settings.cola;
        const ss2Cola = settings?.colaSS2 ?? settings.cola;
        const income1Cola = settings?.colaIncome ?? settings.cola;
        const income2Cola = settings?.colaIncome2 ?? settings.cola;

        const isDuplicate = data.some(
          (row: UPRow) => row.Age.value === newAge && row.Age2?.value === newAge2
        );

        if (isDuplicate) {
          devLog("Duplicate found at: ", i, "Skipping...", "Row: ", gridSet[i]);
          i++;
          continue;
        }

        if (
          settings.person1?.beginAt === gridSet[i].Age.value &&
          !gridSet[i].SocialSecurity1.manualChange
        ) {
          SocialSecurity1 = settings.person1.amount;
        } else if (gridSet[i].SocialSecurity1.manualChange) {
          SocialSecurity1 = gridSet[i].SocialSecurity1.value;
        } else if (
          settings.person1?.beginAt &&
          gridSet[i].Age.value &&
          settings.person1?.beginAt <= gridSet[i].Age.value &&
          !gridSet[i].SocialSecurity1.manualChange
        ) {
          SocialSecurity1 = data[iterator - 1].SocialSecurity1.value * (1 + ss1Cola);
        } else {
          SocialSecurity1 = 0;
        }

        if (
          settings.person2 &&
          settings.person2?.beginAt === gridSet[i].Age2?.value &&
          !gridSet[i].SocialSecurity2.manualChange
        ) {
          SocialSecurity2 = settings.person2.amount;
        } else if (gridSet[i].SocialSecurity2.manualChange) {
          SocialSecurity2 = gridSet[i].SocialSecurity2.value;
        } else if (
          settings.person2?.beginAt &&
          gridSet[i].Age2 &&
          settings.person2?.beginAt <= gridSet[i].Age2?.value &&
          !gridSet[i].SocialSecurity2.manualChange
        ) {
          SocialSecurity2 = data[iterator - 1].SocialSecurity2.value * (1 + ss2Cola);
        } else {
          SocialSecurity2 = 0;
        }

        const SocialSecurity = SocialSecurity1 + SocialSecurity2;

        const Retirement = gridSet[i]?.Retirement.manualChange
          ? (gridSet[i]?.Retirement.value ?? 0)
          : data[iterator - 1]?.YearEnd;

        const ROR = gridSet[i]?.ROR.manualChange
          ? (gridSet[i]?.ROR.value ?? 0)
          : data[iterator - 1].ROR.value;

        const Pension = gridSet[i]?.Pension.manualChange
          ? (gridSet[i]?.Pension.value ?? 0)
          : data[iterator - 1].Pension.value;

        const Spending = gridSet[i]?.Spending.manualChange
          ? (gridSet[i]?.Spending.value ?? 0)
          : data[iterator - 1].Spending.value * (1 + settings.inflation);

        const Spending2 = gridSet[i]?.Spending2.manualChange
          ? (gridSet[i]?.Spending2.value ?? 0)
          : data[iterator - 1].Spending2.value * (1 + settings.inflation);

        const Spending3 = gridSet[i]?.Spending3.manualChange
          ? (gridSet[i]?.Spending3.value ?? 0)
          : data[iterator - 1].Spending3.value * (1 + settings.inflation);

        const Income = gridSet[i]?.Income.manualChange
          ? (gridSet[i].Income.value ?? 0)
          : data[iterator - 1].Income.value * (1 + income1Cola);

        const Income2 = gridSet[i]?.Income2.manualChange
          ? (gridSet[i].Income2.value ?? 0)
          : data[iterator - 1].Income2.value * (1 + income2Cola);

        data.push({
          id: i,
          Age: {
            value: age,
            isIncremented: contactsSwapped
              ? gridSet[i].Age2.value === currentPrimaryAge.value && currentPrimaryAge.isIncremented
              : gridSet[i].Age.value === currentPrimaryAge.value && currentPrimaryAge.isIncremented,
          },
          Age2: {
            value: age2,
            isIncremented: contactsSwapped
              ? gridSet[i].Age.value === currentSecondaryAge?.value &&
                currentSecondaryAge?.isIncremented
              : gridSet[i].Age2.value === currentSecondaryAge?.value &&
                currentSecondaryAge?.isIncremented,
          },
          SocialSecurity: {
            value: SocialSecurity ? SocialSecurity : 0,
            defaultValue: SocialSecurity ? SocialSecurity : 0,
          },
          SocialSecurity1: {
            value: SocialSecurity1 ? SocialSecurity1 : 0,
            defaultValue: gridSet[i].SocialSecurity1?.manualChange
              ? gridSet[i].SocialSecurity1?.defaultValue
              : SocialSecurity1,
            manualChange: gridSet[i].SocialSecurity1.manualChange,
            note: gridSet[i].SocialSecurity1?.note ?? "",
          },
          SocialSecurity2: {
            value: SocialSecurity2 ? SocialSecurity2 : 0,
            defaultValue: gridSet[i].SocialSecurity2?.manualChange
              ? gridSet[i].SocialSecurity2?.defaultValue
              : SocialSecurity2,
            manualChange: gridSet[i].SocialSecurity2.manualChange,
            note: gridSet[i].SocialSecurity2?.note ?? "",
          },
          Retirement: {
            value: Retirement ? Retirement : 0,
            defaultValue: gridSet[i].Retirement?.manualChange
              ? gridSet[i - 1].Retirement?.value
              : Retirement,
            manualChange: gridSet[i]?.Retirement?.manualChange,
            note: gridSet[i]?.Retirement?.note ?? "",
          },
          ROR: {
            value: ROR ? ROR : 0,
            defaultValue: gridSet[i].ROR?.manualChange ? gridSet[i - 1].ROR?.value : ROR,
            manualChange: gridSet[i]?.ROR?.manualChange,
            note: gridSet[i]?.ROR?.note ?? "",
          },
          Pension: {
            value: Pension ? Pension : 0,
            defaultValue: gridSet[i].Pension?.manualChange
              ? gridSet[i - 1].Pension?.value
              : Pension,
            manualChange: gridSet[i]?.Pension?.manualChange,
            note: gridSet[i]?.Pension?.note ?? "",
            isExcluded: gridSet[i].Pension.isExcluded ?? undefined,
          },
          Spending: {
            value: Spending ? Spending : 0,
            defaultValue: gridSet[i].Spending?.manualChange
              ? gridSet[i - 1].Spending?.value * (1 + settings.inflation)
              : Spending,
            manualChange: gridSet[i].Spending?.manualChange,
            note: gridSet[i].Spending?.note ?? "",
            isExcluded: gridSet[i].Spending.isExcluded ?? undefined,
          },
          Spending2: {
            value: Spending2 ? Spending2 : 0,
            defaultValue: gridSet[i].Spending2?.manualChange
              ? gridSet[i - 1].Spending2?.value * (1 + settings.inflation)
              : Spending2,
            manualChange: gridSet[i].Spending2?.manualChange,
            note: gridSet[i].Spending2?.note ?? "",
            isExcluded: gridSet[i].Spending2.isExcluded ?? undefined,
          },
          Spending3: {
            value: Spending3 ? Spending3 : 0,
            defaultValue: gridSet[i].Spending3?.manualChange
              ? gridSet[i - 1].Spending3?.value * (1 + settings.inflation)
              : Spending3,
            manualChange: gridSet[i].Spending3?.manualChange,
            note: gridSet[i].Spending3?.note ?? "",
            isExcluded: gridSet[i].Spending3.isExcluded ?? undefined,
          },
          Income: {
            value: Income,
            defaultValue: gridSet[i].Income?.manualChange
              ? gridSet[i - 1].Income?.value * (1 + income1Cola)
              : Income,
            manualChange: gridSet[i].Income?.manualChange,
            note: gridSet[i].Income?.note ?? "",
            isExcluded: gridSet[i].Income.isExcluded ?? undefined,
          },
          Income2: {
            value: Income2,
            defaultValue: gridSet[i].Income2?.manualChange
              ? gridSet[i - 1].Income2.value * (1 + income2Cola)
              : Income2,
            manualChange: gridSet[i].Income2?.manualChange,
            note: gridSet[i].Income2?.note ?? "",
            isExcluded: gridSet[i].Income2.isExcluded ?? undefined,
          },
          YearEnd:
            Retirement * (1 + ROR) +
            SocialSecurity +
            Pension +
            Income +
            Income2 -
            Spending -
            Spending2 -
            Spending3,
        });
      }

      i++;
      iterator++;
    }

    resolve(null);
  });

  gridSet = gridSet.splice(0, startRow);
  return [...gridSet, ...data];
}

export function calculateAge(date: string | undefined, index = 0, currentDate = new Date()) {
  if (!date || isNaN(Date.parse(date))) {
    return { value: 0, isIncremented: false };
  }

  const dob = new Date(date);
  const currYear = currentDate.getFullYear();
  const currMonth = currentDate.getMonth();
  const currDay = currentDate.getDate();

  let age = currYear - dob.getFullYear();
  let isIncremented = false;

  const wasBirthdayThisYear =
    dob.getMonth() < currMonth || (dob.getMonth() === currMonth && dob.getDate() <= currDay);

  const next3MonthDate = new Date(currYear, currMonth + 3, currDay);
  const dobThisYear = new Date(currYear, dob.getMonth(), dob.getDate());
  const dobNextYear = new Date(currYear + 1, dob.getMonth(), dob.getDate());
  if (!wasBirthdayThisYear) {
    age--;
  }
  const isWithin3Months =
    (dobThisYear > currentDate && dobThisYear <= next3MonthDate) ||
    (dobNextYear > currentDate && dobNextYear <= next3MonthDate);

  if (isWithin3Months) {
    isIncremented = true;
    age++;
  }

  return { value: age + index, isIncremented };
}

function renderP1InitData(clients: Household, index: number) {
  const age = clients?.primaryContact?.birthDate
    ? calculateAge(clients?.primaryContact.birthDate.toString(), index)
    : { value: 101, isIncremented: false };

  if (age.value > 100) {
    return { Age: null };
  }

  return { Age: age };
}

function renderP1Data(clients: Household, index: number) {
  const age = clients?.primaryContact?.birthDate
    ? calculateAge(clients?.primaryContact.birthDate.toString(), index)
    : { value: 101, isIncremented: false };

  if (age.value > 100) {
    return { Age: null };
  }

  return { Age: age };
}

function renderP2InitData(clients: Household, index: number) {
  const age = clients?.secondaryContact?.birthDate
    ? calculateAge(clients?.secondaryContact?.birthDate.toString(), index)
    : { value: 101, isIncremented: false };

  if (age.value > 100) {
    return { Age2: null, Pension2: null, Income2: null };
  }

  return { Age2: age };
}

function renderP2Data(clients: Household, index: number) {
  const age = clients?.secondaryContact?.birthDate
    ? calculateAge(clients?.secondaryContact?.birthDate.toString(), index)
    : { value: 101, isIncremented: false };

  if (age.value > 100) {
    return { Age2: null };
  }

  return { Age2: age };
}
